import { Report } from "@/types/report";
import { formatCurrencyWithNotation } from "@/utils/currency";

/**
 * Get the longest characters from the data
 * @param data - The data to get the longest characters from
 * @returns The longest characters
 */
export const useGetLongestCharacters = (data?: Report): number => {
  if (!data) {
    return 0;
  }

  // Find the longest price from sales
  const salesLongestPrice = (data?.sales?.details || []).reduce(
    (max, detail) => {
      return Math.max(max, detail.final_amount.toString().length);
    },
    0
  );

  // Find the longest price from expenses
  const expensesLongestPrice = (data?.expenses?.details || []).reduce(
    (max, detail) => {
      return Math.max(max, detail.total_amount.toString().length);
    },
    0
  );

  // Calculate totals for sales and expenses
  const totalSales = data?.sales?.final_amount || 0;
  const totalExpenses = data?.expenses?.total_amount || 0;
  const totalSalesCharacters =
    formatCurrencyWithNotation(totalSales).toString().length;
  const totalExpensesCharacters =
    formatCurrencyWithNotation(totalExpenses).toString().length;
  const netProfitCharacters = formatCurrencyWithNotation(
    totalSales - totalExpenses
  ).toString().length;

  return Math.max(
    salesLongestPrice,
    expensesLongestPrice,
    totalSalesCharacters,
    totalExpensesCharacters,
    netProfitCharacters
  );
};

/**
 * Get the minimum card width
 * @param longestCharacters - The longest characters
 * @param multiplier - The multiplier
 * @param extra - The extra
 */
export const useGetMinCardWidth = (
  longestCharacters: number,
  multiplier: number = 13,
  extra: number = 32
) => {
  if (!longestCharacters || longestCharacters <= 4) {
    return 200;
  }

  // get price length with notation
  const priceWithNotation = formatCurrencyWithNotation(10 ** longestCharacters);
  return priceWithNotation.length * multiplier + extra;
};
